import _slicedToArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { providers } from 'ethers';
import { useConfig } from '../../../hooks';
import { ReadonlyNetworksContext } from './context';
import { fromEntries } from '../../../helpers/fromEntries';
import { networkStatesReducer } from './reducer';
import { useWindow } from '../../window';
import { isWebSocketProvider } from '../../../helpers';
var Provider = providers.Provider,
  StaticJsonRpcProvider = providers.StaticJsonRpcProvider;
var getProviderFromConfig = function getProviderFromConfig(urlOrProviderOrProviderFunction) {
  if (Provider.isProvider(urlOrProviderOrProviderFunction)) {
    return urlOrProviderOrProviderFunction;
  }
  if (typeof urlOrProviderOrProviderFunction === 'function') {
    return urlOrProviderOrProviderFunction();
  }
  return new StaticJsonRpcProvider(urlOrProviderOrProviderFunction);
};
export var getProvidersFromConfig = function getProvidersFromConfig(readOnlyUrls) {
  return fromEntries(Object.entries(readOnlyUrls).map(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      chainId = _ref2[0],
      urlOrProviderOrProviderFunction = _ref2[1];
    return [chainId, getProviderFromConfig(urlOrProviderOrProviderFunction)];
  }));
};
export function ReadonlyNetworksProvider(_ref3) {
  var _ref3$providerOverrid = _ref3.providerOverrides,
    providerOverrides = _ref3$providerOverrid === void 0 ? {} : _ref3$providerOverrid,
    children = _ref3.children;
  var _useConfig = useConfig(),
    _useConfig$readOnlyUr = _useConfig.readOnlyUrls,
    readOnlyUrls = _useConfig$readOnlyUr === void 0 ? {} : _useConfig$readOnlyUr,
    pollingInterval = _useConfig.pollingInterval,
    pollingIntervals = _useConfig.pollingIntervals;
  var isActive = useWindow();
  var _useState = useState(function () {
      return Object.assign(Object.assign({}, getProvidersFromConfig(readOnlyUrls)), providerOverrides);
    }),
    _useState2 = _slicedToArray(_useState, 2),
    providers = _useState2[0],
    setProviders = _useState2[1];
  var _useReducer = useReducer(networkStatesReducer, Object.assign({}, fromEntries(Object.keys(Object.assign(Object.assign({}, readOnlyUrls), providerOverrides)).map(function (chainId) {
      return [chainId, {
        errors: []
      }];
    })))),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    networkStates = _useReducer2[0],
    dispatchNetworkState = _useReducer2[1];
  var getPollingInterval = useCallback(function (chainId) {
    var _a;
    return (_a = pollingIntervals === null || pollingIntervals === void 0 ? void 0 : pollingIntervals[chainId]) !== null && _a !== void 0 ? _a : pollingInterval;
  }, [pollingInterval, pollingIntervals]);
  useEffect(function () {
    setProviders(Object.assign(Object.assign({}, getProvidersFromConfig(readOnlyUrls)), providerOverrides));
  }, Object.entries(readOnlyUrls).flat());
  useEffect(function () {
    for (var _i = 0, _Object$entries = Object.entries(readOnlyUrls); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 1),
        chainId = _Object$entries$_i[0];
      var provider = providers[chainId];
      if (provider && !isWebSocketProvider(provider)) {
        provider.polling = isActive;
      }
    }
  }, [isActive, providers, readOnlyUrls]);
  useEffect(function () {
    for (var _i2 = 0, _Object$entries2 = Object.entries(providers); _i2 < _Object$entries2.length; _i2++) {
      var _Object$entries2$_i = _slicedToArray(_Object$entries2[_i2], 2),
        chainId = _Object$entries2$_i[0],
        provider = _Object$entries2$_i[1];
      if (!isWebSocketProvider(provider)) {
        provider.pollingInterval = getPollingInterval(Number(chainId));
      }
    }
  }, [providers, getPollingInterval]);
  var networks = useMemo(function () {
    return {
      providers: providers,
      updateNetworkState: dispatchNetworkState,
      networkStates: networkStates
    };
  }, [providers, dispatchNetworkState, networkStates]);
  return _jsx(ReadonlyNetworksContext.Provider, Object.assign({
    value: networks
  }, {
    children: children
  }));
}